import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaPlane, FaPlus, FaEdit, FaTrash, FaUndo,
  FaTimes, FaSave, FaLock, FaQrcode, FaDownload, FaCamera
} from 'react-icons/fa';
import { QRCodeCanvas } from 'qrcode.react';
import { useApiCall } from '../hooks/useApiCall';
import { useAccount } from '../contexts/AccountContext';
import { useToast } from '../components/Toast';
import { useModal } from '../components/Modal';
import SortTh from '../components/SortTh';
import '../styles/Reference.css';

function fmtTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ─── Aircraft Detail Modal ──────────────────────────────────────────────────
function AircraftDetail({ aircraftId }) {
  const apiCall = useApiCall();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    const res = await apiCall('admin/aircrafts/detail', 'POST', { id: aircraftId, page: p });
    if (res?.success) setData(res.data);
    setLoading(false);
  }, [aircraftId]);

  useEffect(() => { load(1); }, []);

  if (loading && !data) return <div className="table-empty" style={{ padding: 32 }}>Loading…</div>;
  if (!data) return <div className="table-empty" style={{ padding: 32 }}>Failed to load.</div>;

  const { aircraft, stats, transactions, total } = data;
  const pages = Math.ceil(total / 25);

  return (
    <div className="entity-detail">
      <div className="entity-detail-stats">
        <div className="entity-stat">
          <div className="entity-stat-val">{stats.total_txns}</div>
          <div className="entity-stat-label">Transactions</div>
        </div>
        <div className="entity-stat">
          <div className="entity-stat-val">{stats.total_dispensed.toLocaleString()} <span className="entity-stat-unit">{aircraft.gas_type}</span></div>
          <div className="entity-stat-label">Total dispensed</div>
        </div>
        <div className="entity-stat">
          <div className="entity-stat-val">{stats.first_ts ? new Date(stats.first_ts * 1000).toLocaleDateString() : '—'}</div>
          <div className="entity-stat-label">First fueling</div>
        </div>
        <div className="entity-stat">
          <div className="entity-stat-val">{stats.last_ts ? new Date(stats.last_ts * 1000).toLocaleDateString() : '—'}</div>
          <div className="entity-stat-label">Last fueling</div>
        </div>
      </div>

      <div className="entity-detail-txns">
        <div className="entity-detail-txns-header">Transactions</div>
        {transactions.length === 0 ? (
          <div className="table-empty" style={{ padding: 20 }}>No transactions found.</div>
        ) : (
          <>
            <table className="data-table">
              <thead><tr><th>#</th><th>Type</th><th>Tanker</th><th>Amount</th><th>Fueler</th><th>Date</th></tr></thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.id}>
                    <td className="txn-id-cell">#{tx.id}</td>
                    <td><span className="txn-badge txn-badge-out">− OUT</span></td>
                    <td>{tx.tanker || '—'}</td>
                    <td>−{tx.amount} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{tx.amount_type}</span></td>
                    <td>{tx.fueler || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{fmtTs(tx.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pages > 1 && (
              <div className="bc-pagination-row">
                <button className="btn btn-sm btn-secondary" onClick={() => { setPage(p => p - 1); load(page - 1); }} disabled={page === 1}>←</button>
                <span>{page} / {pages}</span>
                <button className="btn btn-sm btn-secondary" onClick={() => { setPage(p => p + 1); load(page + 1); }} disabled={page === pages}>→</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Aircraft Form ────────────────────────────────────────────────────────────
function AircraftForm({ aircraft, gasTypes, onSave, closeModal }) {
  const [form, setForm] = useState(
    aircraft
      ? { id: aircraft.id, name: aircraft.name, gas_type_id: aircraft.gas_type_id }
      : { id: 0, name: '', gas_type_id: gasTypes[0]?.id || 1 }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <form id="aircraft-form" onSubmit={e => { e.preventDefault(); onSave(form); closeModal(); }}>
      <div className="form-row">
        <label className="form-label">Registration / Call sign <span className="required">*</span></label>
        <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. PZ-HVG" />
      </div>
      <div className="form-row">
        <label className="form-label">Fuel type</label>
        <select className="form-input" value={form.gas_type_id} onChange={e => set('gas_type_id', Number(e.target.value))}>
          {gasTypes.map(g => <option key={g.id} value={g.id}>{g.type}</option>)}
        </select>
      </div>
    </form>
  );
}

// ─── QR Panel ─────────────────────────────────────────────────────────────────
function AircraftQRPanel({ aircraft, onImageUploaded }) {
  const { account } = useAccount();
  const { showToast } = useToast();
  const qrRef    = useRef(null);
  const fileRef  = useRef(null);
  const [imgUrl,    setImgUrl]    = useState(aircraft.image_url);
  const [uploading, setUploading] = useState(false);
  const [dragging,  setDragging]  = useState(false);

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `QR_${aircraft.name.replace(/\s+/g, '_')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('id', aircraft.id);
    fd.append('image', file);
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    const API_KEY      = process.env.REACT_APP_API_KEY;
    try {
      const res = await fetch(`${API_BASE_URL}admin/aircrafts/upload-image`, {
        method: 'POST',
        headers: { api: API_KEY, ...(account?.token && { auth: account.token }) },
        body: fd,
      }).then(r => r.json());
      if (res?.success) {
        setImgUrl(res.data.image_url + '?t=' + Date.now());
        onImageUploaded?.();
        showToast('Image uploaded.', 'success');
      } else {
        showToast(res?.response || 'Upload failed.', 'error');
      }
    } catch { showToast('Upload error.', 'error'); }
    setUploading(false);
  };

  const handleFileInput = (e) => uploadFile(e.target.files?.[0]);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) uploadFile(file);
  };

  return (
    <div className="qr-panel">
      <div className="qr-image-section">
        <div
          className={`qr-dropzone${dragging ? ' qr-dropzone--over' : ''}${uploading ? ' qr-dropzone--loading' : ''}`}
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {uploading ? (
            <div className="qr-drop-placeholder"><div className="qr-spinner" /><span>Uploading…</span></div>
          ) : imgUrl ? (
            <>
              <img src={imgUrl} alt={aircraft.name} className="qr-preview-img" />
              <div className="qr-drop-overlay"><FaCamera size={22} /><span>Replace image</span></div>
            </>
          ) : (
            <div className="qr-drop-placeholder">
              <FaCamera size={28} />
              <span>{dragging ? 'Drop to upload' : 'Drag & drop or click'}</span>
              <small>JPEG, PNG, WEBP — max 5 MB</small>
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileInput} />
      </div>
      <div className="qr-code-section" ref={qrRef}>
        <p className="qr-label">{aircraft.name}</p>
        <p className="qr-sub">Token: <code>{aircraft.qr_token}</code></p>
        <QRCodeCanvas value={aircraft.qr_token || aircraft.name} size={180} level="M" includeMargin />
        <button className="btn btn-sm btn-secondary qr-dl-btn" onClick={downloadQR}>
          <FaDownload /> Download QR
        </button>
      </div>
    </div>
  );
}

// ─── RefTable ─────────────────────────────────────────────────────────────────
function RefTable({ items, columns, sortKeys, renderRow, onEdit, onDelete, onRestore, onQR, onRowClick }) {
  const [sortBy,  setSortBy]  = useState(0);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (idx) => {
    setSortBy(idx);
    setSortDir(sortBy === idx && sortDir === 'asc' ? 'desc' : 'asc');
  };

  const sortItems = (list) => {
    if (!sortKeys?.[sortBy]) return list;
    return [...list].sort((a, b) => {
      const key = sortKeys[sortBy];
      const av = a[key] ?? '', bv = b[key] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  };

  const active   = sortItems(items.filter(i => !i.archived));
  const archived = sortItems(items.filter(i =>  i.archived));

  const renderSection = (list, isArchived) => list.map(item => {
    const cells = renderRow(item);
    return (
      <tr key={item.id} className={`${isArchived ? 'ref-row-archived' : ''} ${!isArchived && onRowClick ? 'ref-row-clickable' : ''}`}
          onClick={!isArchived && onRowClick ? () => onRowClick(item) : undefined}>
        {cells.map((c, i) => <td key={i}>{c}</td>)}
        <td>
          {isArchived ? (
            <div className="table-actions">
              <span className="ref-badge-archived">Archived</span>
              <button className="btn btn-sm btn-secondary" title="Restore" onClick={() => onRestore(item)}><FaUndo /></button>
            </div>
          ) : (
            <div className="table-actions">
              {item.txn_count > 0 && <span className="ref-badge-linked" title={`${item.txn_count} linked transaction(s)`}>{item.txn_count} txns</span>}
              {onQR && <button className="btn btn-sm btn-secondary" title="QR & Image" onClick={(e) => { e.stopPropagation(); onQR(item); }}><FaQrcode /></button>}
              <button className="btn btn-sm btn-secondary" onClick={(e) => { e.stopPropagation(); onEdit(item); }}><FaEdit /></button>
              {item.txn_count > 0
                ? <button className="btn btn-sm btn-secondary" disabled title="Cannot delete — linked to transactions" style={{ opacity: 0.4, cursor: 'not-allowed' }}><FaLock /></button>
                : <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); onDelete(item); }}><FaTrash /></button>
              }
            </div>
          )}
        </td>
      </tr>
    );
  });

  return (
    <div className="users-table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((c, i) => sortKeys?.[i]
              ? <SortTh key={c} col={i} label={c} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              : <th key={c}>{c}</th>
            )}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {active.length === 0 && archived.length === 0 ? (
            <tr><td colSpan={columns.length + 1} className="table-empty">No aircraft yet. Add one above.</td></tr>
          ) : (
            <>
              {renderSection(active, false)}
              {archived.length > 0 && (
                <>
                  <tr className="ref-archived-header">
                    <td colSpan={columns.length + 1}>
                      <span className="ref-archived-label">Archived (historical records kept)</span>
                    </td>
                  </tr>
                  {renderSection(archived, true)}
                </>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function Aircrafts() {
  const apiCall = useApiCall();
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();

  const [aircrafts, setAircrafts] = useState([]);
  const [gasTypes,  setGasTypes]  = useState([]);
  const [loading,   setLoading]   = useState(true);

  const load = useCallback(async () => {
    const res = await apiCall('admin/aircrafts', 'POST', {});
    if (res?.success) {
      setAircrafts(res.data.aircrafts || []);
      if (res.data.gas_types?.length) setGasTypes(res.data.gas_types);
    }
  }, [apiCall]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, []);

  const openDetail = (aircraft) => {
    openModal(
      <><FaPlane style={{ marginRight: 8, opacity: 0.7 }} />{aircraft.name}</>,
      <AircraftDetail aircraftId={aircraft.id} />,
      'lg'
    );
  };

  const openForm = (aircraft = null) => {
    openModal(
      aircraft ? 'Edit Aircraft' : 'Add Aircraft',
      <AircraftForm
        aircraft={aircraft}
        gasTypes={gasTypes}
        onSave={async (form) => {
          const res = await apiCall('admin/aircrafts/save', 'POST', { form });
          if (res?.success) { showToast(aircraft ? 'Aircraft updated.' : 'Aircraft added.', 'success'); load(); }
          else showToast(res?.response || 'Error saving aircraft.', 'error');
        }}
        closeModal={closeModal}
      />,
      'sm',
      {
        closeOnBackdrop: false,
        footer: (closeModal) => (
          <>
            <button type="button" className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
            <button type="submit" form="aircraft-form" className="btn btn-primary"><FaSave /> {aircraft ? 'Save changes' : 'Add aircraft'}</button>
          </>
        )
      }
    );
  };

  const openQR = (aircraft) => {
    openModal(
      `${aircraft.name} — QR & Image`,
      <AircraftQRPanel aircraft={aircraft} onImageUploaded={load} />,
      'md'
    );
  };

  const confirmDelete = (aircraft) => {
    openModal(
      'Delete Aircraft',
      <div className="delete-confirmation">
        <p>Permanently delete <strong>"{aircraft.name}"</strong>?</p>
        <p className="warning-text">This cannot be undone.</p>
      </div>,
      'sm',
      {
        footer: (closeModal) => (
          <>
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={async () => {
              const res = await apiCall('admin/aircrafts/delete', 'POST', { id: aircraft.id });
              if (res?.success) { showToast('Aircraft deleted.', 'success'); load(); }
              else showToast(res?.response || 'Error.', 'error');
              closeModal();
            }}>Delete</button>
          </>
        )
      }
    );
  };

  const restore = async (aircraft) => {
    const res = await apiCall('admin/aircrafts/restore', 'POST', { id: aircraft.id });
    if (res?.success) { showToast('Aircraft restored.', 'success'); load(); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><FaPlane style={{ marginRight: 10, opacity: 0.7 }} />Aircraft</h1>
          <p className="page-subtitle">Registered aircraft and helicopter call signs</p>
        </div>
        <button className="btn btn-primary" onClick={() => openForm()}>
          <FaPlus style={{ marginRight: 6 }} /> Add Aircraft
        </button>
      </div>

      {loading ? (
        <div className="table-empty" style={{ padding: 40 }}>Loading…</div>
      ) : (
        <RefTable
          items={aircrafts}
          columns={['Registration', 'Fuel type', 'Transactions']}
          sortKeys={['name', 'gas_type', 'txn_count']}
          renderRow={a => [a.name, a.gas_type || '—', a.txn_count]}
          onEdit={openForm}
          onDelete={confirmDelete}
          onRestore={restore}
          onQR={openQR}
          onRowClick={openDetail}
        />
      )}
    </div>
  );
}
